/**
 * The job colours of the phone apps, in one place.
 *
 * Every tinted thing on these screens — a badge, an icon tile, a button, a timeline dot — picks its
 * colour from here rather than spelling out `bg-success-soft text-success` inline. That is what
 * keeps "green means the parcel arrived" true on all three apps: when a tone is a string the lists
 * pass around, a screen cannot invent a fourth green.
 *
 * `soft` is a real token (see `--success-soft` and friends in main.css), not `success/10`. An alpha
 * tint over the cream page shifted hue with whatever sat under it, so the same chip read differently
 * on a card and on the page.
 *
 * Tailwind scans source text literally, so these have to be whole class names — never
 * `` `bg-${tone}-soft` ``, which produces nothing at build time.
 */
export type PwaTone = 'primary' | 'success' | 'warning' | 'info' | 'neutral'

/** Saturated ink on its own wash — badges, icon tiles, soft buttons. */
export const toneSoft: Record<PwaTone, string> = {
  primary: 'bg-primary-soft text-primary',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  info: 'bg-info-soft text-info',
  neutral: 'bg-muted text-muted-foreground'
}

/** The wash alone, for a surface that carries its own text colour. */
export const toneSoftSurface: Record<PwaTone, string> = {
  primary: 'bg-primary-soft',
  success: 'bg-success-soft',
  warning: 'bg-warning-soft',
  info: 'bg-info-soft',
  neutral: 'bg-muted'
}

/** Filled — the one action a screen came to offer. */
export const toneSolid: Record<PwaTone, string> = {
  primary: 'bg-primary text-primary-foreground',
  success: 'bg-success text-success-foreground',
  warning: 'bg-warning text-warning-foreground',
  info: 'bg-info text-info-foreground',
  neutral: 'bg-foreground text-background'
}

/** Ink only — a heading, an amount, an icon on a plain surface. */
export const toneText: Record<PwaTone, string> = {
  primary: 'text-primary',
  success: 'text-success',
  warning: 'text-warning',
  info: 'text-info',
  neutral: 'text-muted-foreground'
}

/** A hairline in the tone, for a bordered card that is not filled. */
export const toneBorder: Record<PwaTone, string> = {
  primary: 'border-primary/25',
  success: 'border-success/25',
  warning: 'border-warning/25',
  info: 'border-info/25',
  neutral: 'border-border'
}
